import { DyorApi, Sort } from "@api/DyorApi";
import { WalletController } from "@api/WalletController";
import { create } from "zustand";
import { IAsset, IAssetDetail } from "@api/IWalletController";
import Cacher from "@utils/cacher";
import { address } from "@ton/core";

export interface IAssetsStore {
    assets: IAsset[];
    setAssets(assets: IAsset[]): void;
    pushUniqueAssets(assets: IAsset[]): void;
    findAssets(asset: IAssetDetail[]): Promise<IAsset[]>;
    fetchAssets(force?: boolean, retries?: number): Promise<void>;
}

export enum AssetType {
    NATIVE = "native",
    JETTON = "jetton",
    STABLECOIN = "stablecoin",
}

// Кэш для ассетов
const cacher = new Cacher<IAsset[]>("assets.store-cache");

export const initialAssets: IAsset[] = [
    {
        guid: "71e19a07-c3de-43a4-a231-388110a690b6",
        type: AssetType.NATIVE,
        jettonMasterAddress: "ton",
        image: "https://ton.org/download/ton_symbol.svg",
        decimals: 9,
        name: "Toncoin",
        symbol: "TON",
    },
    {
        guid: "173a246d-15af-46d3-a997-33eb2d4f2c57",
        type: AssetType.STABLECOIN,
        jettonMasterAddress: "EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs",
        image: "https://cache.tonapi.io/imgproxy/T3PB4s7oprNVaJkwqbGg54nexKE0zzKhcrPv8jcWYzU/rs:fill:200:200:1/g:no/aHR0cHM6Ly90ZXRoZXIudG8vaW1hZ2VzL2xvZ29DaXJjbGUucG5n.webp",
        decimals: 6,
        name: "Tether USD",
        symbol: "USDT",
        description: "Tether Token for Tether USD",
    },
];

export const useAssets = create<IAssetsStore>()((set, get) => ({
    assets: [...initialAssets],

    setAssets: (assets: IAsset[]) => set({ assets: assets }),

    pushUniqueAssets: (newAssets: IAsset[]) =>
        set((state) => {
            // Merge: keep all existing assets, only add new ones that aren't present
            const mergedAssets = [
                ...state.assets,
                ...newAssets.filter(
                    (newAsset) =>
                        !state.assets.some(
                            (existingAsset) => existingAsset.jettonMasterAddress === newAsset.jettonMasterAddress,
                        ),
                ),
            ];
            return {
                assets: mergedAssets,
            };
        }),

    fetchAssets: async (force?: boolean, retries = 3) => {
        try {
            // Race DYOR API against a 100ms timeout
            const response = await Promise.race([DyorApi.getBestTokens(Sort.trustScore, 20, force)]);

            const responseAddresses: string[] = (response as any).items.map((item: any) => item.friendlyAddress);
            const cachedAssets: IAsset[] = cacher.getCachedData() || [];
            const cachedAddresses: string[] = cachedAssets.map((asset: IAsset) => asset.jettonMasterAddress);

            // Find addresses missing from cache
            const missingAssetsAddresses: string[] = responseAddresses.filter(
                (address: string) => !cachedAddresses.includes(address),
            );

            if (missingAssetsAddresses.length > 0) {
                // Fetch missing assets

                const missingAssets: IAsset[] = await WalletController.findAssets(
                    responseAddresses.map((address: string) => ({ jettonMasterAddress: address })),
                );

                // Update cache and store only for new assets
                cacher.setCache([...cachedAssets, ...missingAssets]);
                get().pushUniqueAssets(missingAssets);
            } else {
                // If all assets are cached, just use cached assets
                const toPush = responseAddresses.map((address: string) =>
                    cachedAssets.find((asset: IAsset) => asset.jettonMasterAddress === address),
                ) as IAsset[];
                get().pushUniqueAssets(toPush);
            }
        } catch (error) {
            await new Promise((r) => setTimeout(r, 1000));
            if (retries === 0) {
                throw error;
            }
            return get().fetchAssets(force, retries - 1);
        }
    },

    findAssets: async (assets: IAssetDetail[]) => {
        const cachedAssets = cacher.getCachedData() || [];

        const foundAssets: IAsset[] = [];
        const notFoundDetails: IAssetDetail[] = [];

        for (const assetDetail of assets) {
            let found = false;

            // Поиск по jettonMasterAddress
            if (assetDetail.jettonMasterAddress) {
                const cachedAsset = cachedAssets.find(
                    (cached) => cached.jettonMasterAddress === assetDetail.jettonMasterAddress,
                );

                if (cachedAsset) {
                    foundAssets.push(cachedAsset);
                    found = true;
                }
            }
            // Поиск по symbol
            else if (assetDetail.symbol) {
                const cachedAsset = cachedAssets.find((cached) => cached.symbol === assetDetail.symbol);

                if (cachedAsset) {
                    foundAssets.push(cachedAsset);
                    found = true;
                }
            } else if (assetDetail.guid) {
                const cachedAsset = cachedAssets.find((cached) => cached.guid === assetDetail.guid);

                if (cachedAsset) {
                    foundAssets.push(cachedAsset);
                    found = true;
                }
            }

            if (!found) {
                notFoundDetails.push(assetDetail);
            }
        }

        let fetchedAssets: IAsset[] = [];

        // Если не все ассеты были найдены в кеше
        if (notFoundDetails.length > 0) {
            // Получаем недостающие ассеты
            fetchedAssets = await WalletController.findAssets(notFoundDetails);

            if (fetchedAssets.length > 0) {
                // Обновляем кеш
                const updatedCache = [...cachedAssets, ...fetchedAssets];
                cacher.setCache(updatedCache);
            }
        }

        // Объединяем найденные в кеше и полученные из API
        const resultAssets = [...foundAssets, ...fetchedAssets];

        // Добавляем новые ассеты в стор
        get().pushUniqueAssets(resultAssets);

        return resultAssets;
    },
}));
